import type { DateTime } from 'luxon';

import Enums from '@core/types/api/Enums';
import type { ReminderUserSettingsRecordData } from '@core/types/api/ReminderUserSettingsRecordData/ReminderUserSettingsRecordData';
import type { EntityType } from '@core/types/search';
import { hasValue } from '@core/utils/equality';

export type ReminderRecordSummary = { id: string; title: string };

export type ReminderBusinessDateResolver = (
  recordData: ReminderUserSettingsRecordData,
) => DateTime | null;

type ReminderRecordTypeConfig = {
  label: string;
  dataKey: string;
  record: (recordData: ReminderUserSettingsRecordData) => ReminderRecordSummary | undefined;
  businessDates: Partial<
    Record<Enums.ReminderUserSettingsBusinessDate, ReminderBusinessDateResolver | null>
  >;
};

export const REMINDER_RECORD_TYPE_CONFIG: Record<
  Enums.ReminderUserSettingsRecordType,
  ReminderRecordTypeConfig
> = {
  [Enums.ReminderUserSettingsRecordType.Project]: {
    label: 'Project',
    dataKey: 'project_id',
    record: ({ project }) =>
      hasValue(project) ? { id: project.id, title: project.name } : undefined,
    businessDates: {
      [Enums.ReminderUserSettingsBusinessDate.BidDate]: ({ project }) =>
        hasValue(project) ? project.bidDate : null,
      [Enums.ReminderUserSettingsBusinessDate.ExpirationDate]: ({ project }) =>
        hasValue(project) ? project.expirationDate : null,
      [Enums.ReminderUserSettingsBusinessDate.EstimatedStartDate]: ({ project }) =>
        hasValue(project) ? project.estimatedStartDate : null,
      [Enums.ReminderUserSettingsBusinessDate.EstimatedEndDate]: ({ project }) =>
        hasValue(project) ? project.estimatedEndDate : null,
      [Enums.ReminderUserSettingsBusinessDate.General]: null,
    },
  },
  [Enums.ReminderUserSettingsRecordType.Quote]: {
    label: 'Quote',
    dataKey: 'quote_id',
    record: ({ quote }) =>
      hasValue(quote) ? { id: quote.id, title: quote.quoteNumber } : undefined,
    businessDates: {
      [Enums.ReminderUserSettingsBusinessDate.ExpirationDate]: ({ quote }) =>
        hasValue(quote) ? quote.expirationDate : null,
      [Enums.ReminderUserSettingsBusinessDate.General]: null,
    },
  },
  [Enums.ReminderUserSettingsRecordType.Activity]: {
    label: 'Activity',
    dataKey: 'activity_id',
    record: ({ activity }) =>
      hasValue(activity)
        ? { id: activity.id, title: `${activity.name} (${activity.activityCategory})` }
        : undefined,
    businessDates: {
      [Enums.ReminderUserSettingsBusinessDate.StartDate]: ({ activity }) =>
        hasValue(activity) ? activity.startTime : null,
      [Enums.ReminderUserSettingsBusinessDate.General]: null,
    },
  },
};

export const ReminderBusinessDatesByRecordType: Record<
  Enums.ReminderUserSettingsRecordType,
  Enums.ReminderUserSettingsBusinessDate[]
> = Object.fromEntries(
  Object.entries(REMINDER_RECORD_TYPE_CONFIG).map(([recordType, config]) => [
    recordType,
    Object.keys(config.businessDates) as Enums.ReminderUserSettingsBusinessDate[],
  ]),
) as Record<Enums.ReminderUserSettingsRecordType, Enums.ReminderUserSettingsBusinessDate[]>;

export const ReminderRecordTypeByEntityType: Partial<
  Record<EntityType, Enums.ReminderUserSettingsRecordType>
> = {
  projects: Enums.ReminderUserSettingsRecordType.Project,
  quotes: Enums.ReminderUserSettingsRecordType.Quote,
  activities: Enums.ReminderUserSettingsRecordType.Activity,
};

export const reminderRecordSummary = (
  recordType: Enums.ReminderUserSettingsRecordType,
  recordData: ReminderUserSettingsRecordData | undefined,
): ReminderRecordSummary | undefined =>
  hasValue(recordData) ? REMINDER_RECORD_TYPE_CONFIG[recordType].record(recordData) : undefined;

export const reminderBusinessDateResolver = (
  recordType: Enums.ReminderUserSettingsRecordType,
  businessDate: Enums.ReminderUserSettingsBusinessDate,
): ReminderBusinessDateResolver | undefined =>
  REMINDER_RECORD_TYPE_CONFIG[recordType].businessDates[businessDate] ?? undefined;
